/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { usePageSpaceKey } from '@confluence/page-context';
import { expValEquals } from '@confluence/feature-experiments';
import { useExperimentalPromotedSpacesQuery } from '@confluence/legacy-schema-bridge/entry-points/useExperimentalPromotedSpacesQuery';

// eslint-disable-next-line graphql-relay-compat/no-experimental-directives -- Read http://go/cfe-no-experimental-directives
import { GetDefaultSpaceQuery } from '../queries/GetDefaultSpaceQuery.experimentalgraphql';
import type {
	GetDefaultSpaceQuery as GetDefaultSpaceQueryType,
	GetDefaultSpaceQueryVariables,
} from '../queries/__types__/GetDefaultSpaceQuery';

import type {
	useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery as useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery$data,
	useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQueryVariables as useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery$variables,
} from './__types__/useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery';

// NOTE: This is a copy of the useSpaceEnabledContentTypes hook from create-blank-fabric-page
// We had to duplicate it here because the original hook could not be imported without creating
// a circular dependency between create-blank-fabric-page and onboarding-live-doc-changeboarding
//
// Do NOT make changes to or re-use this hook, it should be cleaned up once the changeboarding
// feature is deprecated. If you need to re-use this, please look at useSpaceEnabledContentTypes instead.

export const useIsLivePagesAvailableInSpace = () => {
	const [spaceKey] = usePageSpaceKey();

	const { data: defaultSpaceKeyData } = expValEquals(
		'confluence_deprecate_experimental_schema_exp_2',
		'isEnabled',
		true,
	)
		? // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useExperimentalPromotedSpacesQuery({
				skip: spaceKey !== undefined,
				variables: {
					promotedSpacesLimit: 1,
					otherSpacesLimit: 1,
					promotedSpaceKey: null,
				},
			})
		: // eslint-disable-next-line react-hooks/rules-of-hooks -- The value of expValEquals() does not change over the app's lifetime
			useQuery<GetDefaultSpaceQueryType, GetDefaultSpaceQueryVariables>(
				// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Experimental Schema GraphQL Operation
				GetDefaultSpaceQuery,
				{
					skip: spaceKey !== undefined,
				},
			);

	const { data: enabledContentTypesData } = useQuery<
		useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery$data,
		useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery$variables
	>(
		gql`
			query useIsLivePagesAvailableInSpaceSpaceEnabledContentTypesQuery($spaceKey: String) {
				space(key: $spaceKey) {
					id
					key
					spaceTypeSettings {
						enabledContentTypes {
							isBlogsEnabled
							isDatabasesEnabled
							isEmbedsEnabled
							isLivePagesEnabled
							isWhiteboardsEnabled
							isFoldersEnabled
						}
					}
				}
			}
		`,
		{
			errorPolicy: 'all',
			skip: !spaceKey && !defaultSpaceKeyData,
			variables: {
				spaceKey: spaceKey || defaultSpaceKeyData?.experimentalPromotedSpaces?.nodes?.[0]?.id,
			},
		},
	);

	return enabledContentTypesData
		? enabledContentTypesData?.space?.spaceTypeSettings?.enabledContentTypes?.isLivePagesEnabled
		: false;
};
